import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { InspectionCategoryDetailDto } from '../dtos/inspectionCategoryDetailDto';
import { I18nTextPipe } from '../core/pipes/i18n-text.pipe';
import { ActivatedRoute } from '@angular/router';
import { Point } from '../core/api/model/point';
import { ClientConfigDBService } from '../core/db/client-config-db-service';
import {
  InspectionElementDto,
  I18NText,
  FlatInspectionResultDto,
  TextFieldValueSuggestion,
} from '../core/api/model/models';
import { PointConfig } from '../core/api/model/pointConfig';
import { InspectionResultType } from '../core/api/model/inspectionResultType';
import { PointGroupType } from '../core/api/model/pointGroupType';
import { InspectionResultElementType } from '../core/api/model/inspectionResultElementType';
import { InspectionCategoryCacheService } from '@core/cache/inspection-category-cache-service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InspectionResultMatrixRowPointDto } from '../dtos/inspectionResultMatrixRowPointDto';
import { InfoDialogComponent } from '../info-dialog/info-dialog.component';

@Component({ template: '' })
export class DetailBaseComponent implements OnInit {
  protected i18nTextPipe = inject(I18nTextPipe);
  private route = inject(ActivatedRoute);
  private clientConfigDBService = inject(ClientConfigDBService);
  private inspectionCategoryCacheService = inject(InspectionCategoryCacheService);
  private modalService = inject(NgbModal);

  @Input()
  public categoryDetail!: InspectionCategoryDetailDto;

  @Input()
  public inspectionCategory!: InspectionElementDto;

  @Input()
  public notApplicableCategoriesDisplayed = false;

  @Input()
  public readonly = false;

  @Output()
  public save: EventEmitter<any> = new EventEmitter();

  public hasDefects: boolean = false;

  protected pointConfig!: PointConfig;

  protected copyNumberValueEnabled: boolean = false;

  private hasStickyRows: boolean = false;

  private hasStickyCols: boolean = false;

  public hasDescription(description: I18NText | undefined | null): boolean {
    if (!description) {
      return false;
    }
    return !!this.i18nTextPipe.transform(description);
  }

  public openPointDescriptionDialog(point: InspectionResultMatrixRowPointDto): void {
    const infoCloseModal = this.modalService.open(InfoDialogComponent, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'modal-dialog-md',
    });
    infoCloseModal.componentInstance.title = '';
    infoCloseModal.componentInstance.text = this.i18nTextPipe.transform(point.pointDescription);
  }

  public ngOnInit(): void {
    this.hasDefects =
      this.categoryDetail.categoryResult.results?.some(
        (result) =>
          result.inspectionElementKey !== this.inspectionCategory.key &&
          result.inspectionResult === InspectionResultType.Defect,
      ) ?? false;
    this.pointConfig = this.route.snapshot.data['pointConfig'] as PointConfig;
    this.clientConfigDBService.getClientConfig().subscribe(
      (clientConfig) => {
        if (clientConfig) {
          this.hasStickyRows = clientConfig?.resultWithStickyRows ?? false;
          this.hasStickyCols = clientConfig?.resultWithStickyCols ?? false;
        }
      },
      () => {},
    );
    this.copyNumberValueEnabled = this.inspectionCategoryCacheService.getCopyNumberValueEnabled(
      this.categoryDetail.categoryNumber,
    );
  }

  protected handleDefects(): void {
    const categoryResult = this.categoryDetail.categoryResult.results?.find(
      (result) => result.inspectionElementKey === this.inspectionCategory.key,
    );
    this.hasDefects =
      this.categoryDetail.categoryResult.results?.some(
        (result) =>
          result.inspectionElementKey !== this.inspectionCategory.key &&
          result.inspectionResult === InspectionResultType.Defect,
      ) ?? false;
    if (!categoryResult) {
      return;
    }
    if (categoryResult.inspectionResult === InspectionResultType.Defect && !this.hasDefects) {
      categoryResult.inspectionResult = InspectionResultType.NotDefined;
    } else if (categoryResult.inspectionResult !== InspectionResultType.Defect && this.hasDefects) {
      categoryResult.inspectionResult = InspectionResultType.Defect;
    }
  }

  protected getInspectionElement(
    currentElement: InspectionElementDto,
    key: string | null | undefined,
  ): InspectionElementDto | null {
    let inspectionElement: InspectionElementDto | null = null;
    if (currentElement.key === key) {
      inspectionElement = currentElement;
    } else if (currentElement.childElements) {
      currentElement.childElements.forEach((childElement) => {
        const elementFound = this.getInspectionElement(childElement, key);
        if (elementFound) {
          inspectionElement = elementFound;
        }
      });
    }

    return inspectionElement;
  }

  protected getResult(key: string): FlatInspectionResultDto | undefined {
    return this.categoryDetail.categoryResult.results?.find(
      (result) => result.inspectionElementKey === key,
    );
  }

  protected propagateResultToChildren(
    inspectionElement: InspectionElementDto | null | undefined,
    inspectionResult: InspectionResultType | null | undefined,
    overrideNotApplicable: boolean = false,
  ): void {
    if (inspectionElement?.childElements) {
      inspectionElement.childElements.forEach((childInspectionElement) => {
        if (childInspectionElement.pointGroupType !== PointGroupType.SubheadWithoutDataEntry) {
          const existingResult = this.getResult(childInspectionElement.key);
          if (existingResult && !existingResult.fieldValue) {
            if (
              overrideNotApplicable ||
              existingResult.inspectionResult !== InspectionResultType.NotApplicable
            ) {
              // @ts-expect-error: strict null check ignored intentionally
              existingResult.inspectionResult = inspectionResult ?? null;
              existingResult.remark = null;
              existingResult.defectDescription = null;
            }
          }
        }
        this.propagateResultToChildren(
          childInspectionElement,
          inspectionResult,
          overrideNotApplicable,
        );
      });
    }
  }

  protected isResultNotApplicableOrNotControlled(result: FlatInspectionResultDto): boolean {
    return (
      result.inspectionResult === InspectionResultType.NotApplicable ||
      result.inspectionResult === InspectionResultType.NotControlled
    );
  }

  protected resetFieldValue(result: FlatInspectionResultDto) {
    if (result.fieldValue) {
      result.fieldValue.dateValue = null;
      result.fieldValue.numberValue = null;
      result.fieldValue.textValue = null;
      result.fieldValue.yesNoValue = null;
    }
  }

  protected getDefectDescriptionSuggestions(
    pointGroupNumber: string | null | undefined,
    pointNumber: string | null | undefined,
  ): I18NText[] | null {
    let defectDescriptionSuggestions: I18NText[] | null = null;
    if (this.pointConfig && this.pointConfig.points) {
      const pointConfig: Point[] = this.getPointConfig(pointNumber, pointGroupNumber);
      if (pointConfig.length > 0 && pointConfig[0].defectDescriptionSuggestions) {
        defectDescriptionSuggestions = pointConfig[0].defectDescriptionSuggestions.map((d) => ({
          germanText: d.textDe,
          frenchText: d.textFr,
          italianText: '',
        }));
      }
    }
    return defectDescriptionSuggestions;
  }

  protected getTextFieldValueSuggestions(
    pointGroupNumber: string | null | undefined,
    pointNumber: string | null | undefined,
  ): I18NText[] | null {
    let textFieldValueSuggestions: I18NText[] | null = null;
    if (this.pointConfig && this.pointConfig.points) {
      const pointConfig: Point[] = this.getPointConfig(pointNumber, pointGroupNumber);
      if (pointConfig.length > 0 && pointConfig[0].textFieldValueSuggestions) {
        textFieldValueSuggestions = pointConfig[0].textFieldValueSuggestions.map(
          (d: TextFieldValueSuggestion) => ({
            germanText: d.textDe,
            frenchText: d.textFr,
            italianText: '',
          }),
        );
      }
    }
    return textFieldValueSuggestions;
  }

  protected getIsTextSetToReadonly(
    pointGroupNumber: string | null | undefined,
    pointNumber: string | null | undefined,
  ): boolean {
    let isTextReadonly = false;
    if (this.pointConfig && this.pointConfig.points) {
      const pointConfig: Point[] = this.getPointConfig(pointNumber, pointGroupNumber);
      if (pointConfig.length > 0) {
        isTextReadonly = pointConfig.every((v) => v.isEditable === false);
      }
    }
    return isTextReadonly;
  }

  protected getDisplayRemarkOfFieldValue(
    pointGroupNumber: string | null | undefined,
    pointNumber: string | null | undefined,
  ): boolean {
    if (this.pointConfig && this.pointConfig.points) {
      const pointConfig: Point[] = this.getPointConfig(pointNumber, pointGroupNumber);
      if (pointConfig.length > 0 && pointConfig[0].displayRemarkOfFieldValue === true) {
        return true;
      }
    }
    return false;
  }

  protected isStickyRow(): boolean {
    return this.hasStickyRows;
  }

  protected isStickyCol(): boolean {
    return this.hasStickyCols;
  }

  protected setCategoryResultIfAllPointsAreSame(
    categoryResult: FlatInspectionResultDto | undefined,
    inspectionResult: InspectionResultType | null | undefined,
  ): boolean {
    const points = this.getAllPointsOf(this.inspectionCategory);
    const childResults = this.categoryDetail.categoryResult.results?.filter((result) =>
      points.find((p) => p.key === result.inspectionElementKey && !p.fieldType),
    );
    const childResultsWithSameResult = childResults?.filter(
      (result) => result.inspectionResult === inspectionResult,
    );
    if (childResults?.length === childResultsWithSameResult?.length && categoryResult) {
      // @ts-expect-error: strict null check ignored intentionally
      categoryResult.inspectionResult = inspectionResult;
      return true;
    }
    return false;
  }

  private getPointConfig(
    pointNumber: string | null | undefined,
    pointGroupNumber: string | null | undefined,
  ): Point[] {
    let pointConfig: Point[];
    const pointConfigCategoryPoint = this.pointConfig.points?.filter(
      (p) => p.categoryNumber === this.inspectionCategory.number && p.pointNumber === pointNumber,
    );
    if (!pointConfigCategoryPoint) {
      return [];
    }
    pointConfig = pointConfigCategoryPoint.filter((p) => p.pointGroupNumber === pointGroupNumber);
    if (pointConfig.length === 0) {
      pointConfig = pointConfigCategoryPoint.filter((p) => p.pointGroupNumber === null);
    }
    return pointConfig;
  }

  private getAllPointsOf(inspectionElement: InspectionElementDto): InspectionElementDto[] {
    const childElements = new Array<InspectionElementDto>();
    if (
      (inspectionElement.type === InspectionResultElementType.PointGroup ||
        inspectionElement.type === InspectionResultElementType.Category) &&
      inspectionElement.childElements
    ) {
      inspectionElement.childElements.forEach((child) => {
        const childresults = this.getAllPointsOf(child);
        childresults.forEach((r) => childElements.push(r));
      });
    }
    if (inspectionElement.type === InspectionResultElementType.Point) {
      childElements.push(inspectionElement);
    }
    return childElements;
  }
}
